import { useState, useMemo, useCallback, useEffect } from 'react';
import {
    Box,
    CircularProgress,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { getDocumentStatusMoves, getDocumentStatusMovesByPerson } from 'api/rest/analytics';
import { AppDocStatus } from 'api/models/AppDocStatus';
import { formatLocalizedShortDate } from 'utils/formatDate';
import useAnalyticsData from '../../hooks/useAnalyticsData';
import type {
    DocumentStatusMovesSummaryData,
    DocumentStatusMovesDrilldownData,
    DocumentStatusMoveDetail,
    DocumentStatusMoveAgentSummary,
} from 'hooks/analytics';
import {
    AnalyticsColors,
    SectionCard,
    SectionTitle,
    stateMessageSx,
    loadingWrapSx,
    thSx,
    tdSx,
    trHoverSx,
} from '../../components/analyticsUi';
import { OPERATIONS_AGENT_DOCUMENT_STATUSES } from '../../constants/metricDescriptions';
import InfoTooltip from 'components/v2/common/infoTooltip';

// ---------------------------------------------------------------------------
// Helpers — full status set matches AppDocStatus / backend DocStatus
// ---------------------------------------------------------------------------

/** Canonical column order from AppDocStatus (same as backend DocStatus). */
const APP_DOC_STATUS_COLUMNS: string[] = Object.values(AppDocStatus);

const STATUS_LABELS: Record<string, string> = {
    [AppDocStatus.MISSING]: 'Missing',
    [AppDocStatus.FIX_REQUIRED]: 'Fix Required',
    [AppDocStatus.WAITING_FOR_APPROVAL]: 'Waiting for Approval',
    [AppDocStatus.SIGNATURE_REQUIRED]: 'Signature Required',
    [AppDocStatus.PREAPPROVED]: 'Preapproved',
    [AppDocStatus.APPROVED]: 'Approved',
    [AppDocStatus.AUTO_FILLED]: 'Auto Filled',
    [AppDocStatus.SENT_TO_CLIENT]: 'Sent to Client',
    [AppDocStatus.CLIENT_COMPLETED]: 'Client Completed',
    [AppDocStatus.PRE_APPROVED]: 'Pre-Approved',
};

type SortDirection = 'asc' | 'desc';

const SortIndicator = ({ direction }: { direction: SortDirection }) => (
    <Box
        component="span"
        sx={{ fontSize: 10, lineHeight: 1, color: AnalyticsColors.primary, ml: '4px' }}
    >
        {direction === 'asc' ? '▲' : '▼'}
    </Box>
);

const formatStatusLabel = (status: string | null | undefined) => {
    if (!status) return '—';
    return STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
};

const statusColor = (status: string | null | undefined) => {
    switch (status) {
        case AppDocStatus.FIX_REQUIRED:
            return AnalyticsColors.warning;
        case AppDocStatus.PREAPPROVED:
        case AppDocStatus.PRE_APPROVED:
        case AppDocStatus.APPROVED:
            return AnalyticsColors.success;
        case AppDocStatus.WAITING_FOR_APPROVAL:
        case AppDocStatus.SENT_TO_CLIENT:
            return AnalyticsColors.secondary;
        case AppDocStatus.MISSING:
            return AnalyticsColors.danger;
        case AppDocStatus.SIGNATURE_REQUIRED:
        case AppDocStatus.AUTO_FILLED:
        case AppDocStatus.CLIENT_COMPLETED:
            return AnalyticsColors.primaryMuted;
        default:
            return AnalyticsColors.textPrimary;
    }
};

/**
 * Matrix columns: full AppDocStatus set first (stable order), then any
 * unexpected labels returned by the API that are not in the enum.
 */
const resolveStatusColumns = (data: DocumentStatusMovesSummaryData): string[] => {
    const fromApi = data.statusColumns ?? [];
    const known = new Set(APP_DOC_STATUS_COLUMNS);
    const extras = fromApi.filter((s) => !known.has(s));
    return [...APP_DOC_STATUS_COLUMNS, ...extras];
};

const kpiCardSx = {
    flex: '1 1 140px',
    background: AnalyticsColors.primaryLight,
    borderRadius: '10px',
    p: '1.1rem 1.25rem',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
};

const KpiCard = ({ label, value }: { label: string; value: number }) => (
    <Box sx={kpiCardSx}>
        <Typography
            sx={{
                fontSize: 11,
                fontWeight: 600,
                color: AnalyticsColors.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
            }}
        >
            {label}
        </Typography>
        <Typography
            sx={{
                fontSize: 24,
                fontWeight: 700,
                color: AnalyticsColors.primary,
                fontVariantNumeric: 'tabular-nums',
            }}
        >
            {value.toLocaleString()}
        </Typography>
    </Box>
);

const resolveAgents = (data: DocumentStatusMovesSummaryData): DocumentStatusMoveAgentSummary[] =>
    data.agents ?? data.persons ?? [];

const resolveAgentId = (agent: DocumentStatusMoveAgentSummary) =>
    agent.agentId ?? agent.personId ?? 0;

const resolveAgentName = (agent: DocumentStatusMoveAgentSummary) =>
    agent.agentName ?? agent.personName ?? 'Unknown';

const resolveAgentEmail = (agent: DocumentStatusMoveAgentSummary) =>
    agent.agentEmail ?? agent.personEmail ?? '';

// ---------------------------------------------------------------------------
// Agent × status matrix
// ---------------------------------------------------------------------------

type MatrixSortKey = 'agentName' | 'totalStatusMoves' | string;

interface MatrixTableProps {
    data: DocumentStatusMovesSummaryData;
    onAgentClick: (agentId: number, agentName: string) => void;
}

const AgentStatusMatrix = ({ data, onAgentClick }: MatrixTableProps) => {
    const statusColumns = resolveStatusColumns(data);
    const agents = resolveAgents(data);

    const [sortKey, setSortKey] = useState<MatrixSortKey>('totalStatusMoves');
    const [sortDir, setSortDir] = useState<SortDirection>('desc');

    const handleSort = (key: MatrixSortKey) => {
        if (key === sortKey) {
            setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        } else {
            setSortKey(key);
            setSortDir(key === 'agentName' ? 'asc' : 'desc');
        }
    };

    const sorted = useMemo(() => {
        const copy = [...agents];
        copy.sort((a, b) => {
            let diff = 0;
            if (sortKey === 'agentName') {
                diff = resolveAgentName(a).localeCompare(resolveAgentName(b));
            } else if (sortKey === 'totalStatusMoves') {
                diff = a.totalStatusMoves - b.totalStatusMoves;
            } else {
                diff = (a.statusCounts?.[sortKey] ?? 0) - (b.statusCounts?.[sortKey] ?? 0);
            }
            return sortDir === 'desc' ? -diff : diff;
        });
        return copy;
    }, [agents, sortKey, sortDir]);

    const renderTh = (label: string, key: MatrixSortKey, align?: 'left' | 'right') => (
        <Box
            component="th"
            sx={thSx({ align: align ?? 'left', sortable: true, active: sortKey === key })}
            onClick={() => handleSort(key)}
            style={{ cursor: 'pointer' }}
        >
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '2px',
                    justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
                    width: '100%',
                    whiteSpace: 'nowrap',
                }}
            >
                {label}
                {sortKey === key && <SortIndicator direction={sortDir} />}
            </Box>
        </Box>
    );

    const statusTotals = data.summary.statusTotals ?? {};
    const agentCount = data.summary.agentCount ?? data.summary.personCount ?? agents.length;

    // KPI strip: overall + key operational statuses from AppDocStatus.
    const kpiStatuses = [
        AppDocStatus.FIX_REQUIRED,
        AppDocStatus.WAITING_FOR_APPROVAL,
        AppDocStatus.PREAPPROVED,
        AppDocStatus.PRE_APPROVED,
        AppDocStatus.APPROVED,
    ];

    return (
        <>
            <Box sx={{ display: 'flex', gap: '0.75rem', mb: '1.25rem', flexWrap: 'wrap' }}>
                <KpiCard label="Total Status Moves" value={data.summary.totalStatusMoves} />
                <KpiCard label="Agents" value={agentCount} />
                {kpiStatuses.map((status) => (
                    <KpiCard
                        key={status}
                        label={formatStatusLabel(status)}
                        value={statusTotals[status] ?? 0}
                    />
                ))}
            </Box>

            {sorted.length === 0 ? (
                <Box sx={stateMessageSx}>No document status changes for this period.</Box>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <Box
                        component="table"
                        sx={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            minWidth: 640 + statusColumns.length * 110,
                        }}
                    >
                        <thead>
                            <tr>
                                {renderTh('Agent', 'agentName')}
                                {statusColumns.map((status) =>
                                    renderTh(formatStatusLabel(status), status, 'right')
                                )}
                                {renderTh('Total', 'totalStatusMoves', 'right')}
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((agent) => {
                                const agentId = resolveAgentId(agent);
                                const agentName = resolveAgentName(agent);
                                return (
                                    <Box
                                        component="tr"
                                        key={agentId}
                                        sx={{
                                            ...trHoverSx,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                background: AnalyticsColors.primaryLight,
                                            },
                                        }}
                                        onClick={() => onAgentClick(agentId, agentName)}
                                    >
                                        <Box component="td" sx={tdSx()}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '2px',
                                                }}
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: AnalyticsColors.primary,
                                                    }}
                                                >
                                                    {agentName}
                                                </Box>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        fontSize: 12,
                                                        color: AnalyticsColors.textSecondary,
                                                    }}
                                                >
                                                    {resolveAgentEmail(agent)}
                                                </Box>
                                            </Box>
                                        </Box>
                                        {statusColumns.map((status) => {
                                            const count = agent.statusCounts?.[status] ?? 0;
                                            return (
                                                <Box
                                                    component="td"
                                                    key={status}
                                                    sx={tdSx({
                                                        align: 'right',
                                                        bold: count > 0,
                                                    })}
                                                >
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            color:
                                                                count > 0
                                                                    ? statusColor(status)
                                                                    : AnalyticsColors.textTertiary,
                                                        }}
                                                    >
                                                        {count.toLocaleString()}
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                        <Box
                                            component="td"
                                            sx={tdSx({ align: 'right', bold: true })}
                                        >
                                            {agent.totalStatusMoves.toLocaleString()}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </tbody>
                    </Box>
                </Box>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Detail modal — document list + status
// ---------------------------------------------------------------------------

type DetailSortKey = 'documentName' | 'clientName' | 'programName' | 'status' | 'occurredAt';

interface DetailModalProps {
    open: boolean;
    agentName: string;
    loading: boolean;
    error: string | null;
    data: DocumentStatusMovesDrilldownData | null;
    onClose: () => void;
}

const AgentDocumentsModal = ({
    open,
    agentName,
    loading,
    error,
    data,
    onClose,
}: DetailModalProps) => {
    const [sortKey, setSortKey] = useState<DetailSortKey>('occurredAt');
    const [sortDir, setSortDir] = useState<SortDirection>('desc');

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const documents: DocumentStatusMoveDetail[] = data?.documents ?? data?.moves ?? [];
    const total = data?.totalDocuments ?? data?.totalMoves ?? documents.length;
    const agentEmail =
        data?.agent?.agentEmail ?? data?.person?.personEmail ?? data?.person?.agentEmail ?? '—';

    const handleSort = (key: DetailSortKey) => {
        if (key === sortKey) {
            setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        } else {
            setSortKey(key);
            setSortDir(key === 'occurredAt' ? 'desc' : 'asc');
        }
    };

    const sorted = useMemo(() => {
        const copy = [...documents];
        copy.sort((a, b) => {
            let diff = 0;
            if (sortKey === 'occurredAt') {
                diff = (a.occurredAt ?? '').localeCompare(b.occurredAt ?? '');
            } else if (sortKey === 'status') {
                const aStatus = a.status ?? a.statusTo ?? '';
                const bStatus = b.status ?? b.statusTo ?? '';
                diff = aStatus.localeCompare(bStatus);
            } else {
                diff = (a[sortKey] ?? '').localeCompare(b[sortKey] ?? '');
            }
            return sortDir === 'desc' ? -diff : diff;
        });
        return copy;
    }, [documents, sortKey, sortDir]);

    const renderTh = (label: string, key: DetailSortKey) => (
        <Box
            component="th"
            sx={thSx({ sortable: true, active: sortKey === key })}
            onClick={() => handleSort(key)}
            style={{ cursor: 'pointer' }}
        >
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                {label}
                {sortKey === key && <SortIndicator direction={sortDir} />}
            </Box>
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pr: 1,
                    pb: 1,
                }}
            >
                <Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                        {agentName || data?.agent?.agentName || data?.person?.personName || 'Agent'}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: AnalyticsColors.textSecondary }}>
                        {agentEmail}
                        {` · ${total.toLocaleString()} document${total !== 1 ? 's' : ''}`}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" aria-label="Close">
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: 2, pb: 2 }}>
                {loading ? (
                    <Box sx={loadingWrapSx}>
                        <CircularProgress size={28} />
                    </Box>
                ) : error ? (
                    <Box sx={stateMessageSx}>{error}</Box>
                ) : sorted.length === 0 ? (
                    <Box sx={stateMessageSx}>
                        No documents for this agent in the selected period.
                    </Box>
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box
                            component="table"
                            sx={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                minWidth: 720,
                            }}
                        >
                            <thead>
                                <tr>
                                    {renderTh('Document', 'documentName')}
                                    {renderTh('Client', 'clientName')}
                                    {renderTh('Program', 'programName')}
                                    {renderTh('Status', 'status')}
                                    {renderTh('Changed At', 'occurredAt')}
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map((doc) => {
                                    const status = doc.status ?? doc.statusTo;
                                    return (
                                        <Box component="tr" key={doc.eventId} sx={trHoverSx}>
                                            <Box component="td" sx={tdSx()}>
                                                {doc.documentName ?? '—'}
                                            </Box>
                                            <Box component="td" sx={tdSx()}>
                                                {doc.clientName ?? '—'}
                                            </Box>
                                            <Box component="td" sx={tdSx()}>
                                                {doc.programName ?? '—'}
                                            </Box>
                                            <Box component="td" sx={tdSx()}>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: statusColor(status),
                                                    }}
                                                >
                                                    {formatStatusLabel(status)}
                                                </Box>
                                            </Box>
                                            <Box component="td" sx={tdSx()}>
                                                {formatLocalizedShortDate(doc.occurredAt, {
                                                    includeTime: true,
                                                })}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </tbody>
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

interface AgentDocumentStatusesProps {
    from?: string;
    to?: string;
    serviceProviderId?: string;
}

const AgentDocumentStatuses = ({ from, to, serviceProviderId }: AgentDocumentStatusesProps) => {
    const [selectedAgent, setSelectedAgent] = useState<{
        id: number;
        name: string;
    } | null>(null);
    const [detailData, setDetailData] = useState<DocumentStatusMovesDrilldownData | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState<string | null>(null);

    const summary = useAnalyticsData<DocumentStatusMovesSummaryData>(
        () => getDocumentStatusMoves(from, to, serviceProviderId),
        [from, to, serviceProviderId]
    );

    useEffect(() => {
        if (!selectedAgent) {
            setDetailData(null);
            setDetailError(null);
            setDetailLoading(false);
            return undefined;
        }

        let cancelled = false;
        setDetailLoading(true);
        setDetailError(null);
        setDetailData(null);

        getDocumentStatusMovesByPerson(selectedAgent.id, from, to, serviceProviderId)
            .then((res) => {
                if (!cancelled) setDetailData(res.data);
            })
            .catch((err) => {
                if (!cancelled) {
                    setDetailError(
                        err?.response?.data?.message ||
                            err.message ||
                            'Failed to load agent documents.'
                    );
                }
            })
            .finally(() => {
                if (!cancelled) setDetailLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAgent?.id, from, to, serviceProviderId]);

    const handleAgentClick = useCallback((agentId: number, agentName: string) => {
        setSelectedAgent({ id: agentId, name: agentName });
    }, []);

    const handleCloseModal = useCallback(() => {
        setSelectedAgent(null);
    }, []);

    return (
        <SectionCard>
            <SectionTitle>
                Agent Document Statuses
                <InfoTooltip title={OPERATIONS_AGENT_DOCUMENT_STATUSES} />
            </SectionTitle>

            {summary.loading ? (
                <Box sx={loadingWrapSx}>
                    <CircularProgress size={28} />
                </Box>
            ) : summary.error ? (
                <Box sx={stateMessageSx}>{summary.error}</Box>
            ) : summary.data ? (
                <AgentStatusMatrix data={summary.data} onAgentClick={handleAgentClick} />
            ) : null}

            <AgentDocumentsModal
                open={selectedAgent !== null}
                agentName={selectedAgent?.name ?? ''}
                loading={detailLoading}
                error={detailError}
                data={detailData}
                onClose={handleCloseModal}
            />
        </SectionCard>
    );
};

export default AgentDocumentStatuses;
